import { Component, OnInit } from '@angular/core';
import { DecksService, DeckWithCardSizes } from '../decks.service';
import { CardService } from '../../cards/card.service';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, flatMap, map } from 'rxjs/operators';
import { Card, Deck, Query } from '@deckplan.io/common';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { FormatFactory } from '../formats/format-factory';
import { Format } from '../formats/format.interface';
import { CommonColumns } from '../common/common-columns';
import { PageChanges } from '../../cards/card-table/card-table.component';
import { Utils } from '../common/utils';
import { AuthService } from '../../auth/auth.service';
import { User } from '@deckplan.io/common/users';
import { UsersService } from '../../users/users.service';

@Component({
    selector: 'app-public-deck-view',
    templateUrl: './public-deck-view.component.html',
    styleUrls: ['./public-deck-view.component.scss']
})
export class PublicDeckViewComponent implements OnInit {

    public deck: DeckWithCardSizes;
    public focusedCard = new BehaviorSubject<Card>(null);

    public typeCompositions: Observable<any>;
    public colorStats: Observable<any[]>;
    public cmcHisto: Observable<any>;
    public manaBalance: Observable<any>;

    public showSummary = true;

    public readonly columns = [{selected: true, field: {title: 'Quantity', field: 'quantityInDeck', dataType: 'number', isInput: false}}, ...CommonColumns];

    public queryUpdated = new BehaviorSubject<Query>(null);
    public pageUpdated = new BehaviorSubject<PageChanges>(null);

    public format: Format = null;
    public invalidCards = new Set<string>();

    public currentUser: User;
    public likeButtonColor = 'white';
    public likeIconName = 'favorite_border';

    constructor(private readonly decksService: DecksService,
                private readonly cardsService: CardService,
                private readonly route: ActivatedRoute,
                public readonly authService: AuthService,
                private readonly usersService: UsersService,
                private readonly router: Router) { }

    public cardValidator(card: Card): boolean {
        return !this.invalidCards.has(card.name);
    }


    public ngOnInit(): void {
        this.updateCurrentUser();

        combineLatest([
            this.queryUpdated,
            this.pageUpdated.pipe(flatMap(pageChange => of(Utils.PageOrDefault(pageChange)))),
            this.route.params.pipe(filter(paramMap => paramMap.id))
        ]
        ).pipe(flatMap(results => {
            const query = results[0];
            Utils.TranslateOwnedFieldFilters(query);
            const pagination = results[1];
            // eslint-disable-next-line no-magic-numbers
            const paramMap = results[2];
            return this.decksService.get(paramMap.id, this.deckQuery(query), pagination);
        })).subscribe(this.handleDeckUpdate.bind(this));
    }

    private updateCurrentUser() {
        if (this.authService.currentUserId === 'anonymous') {
            this.currentUser = undefined;
        } else {
            this.usersService.getOne(this.authService.currentUserId).subscribe((user) => this.currentUser = user);
        }
    }

    private deckQuery(query: Query): Query {
        if (!query && !this.format) {
            return null;
        } else if (!query) {
            return this.format.deckCardsQuery();
        } else if (!this.format) {
            return query;
        } else {
            return {
                operator: 'and',
                operands: [
                    query,
                    this.format.deckCardsQuery()
                ]
            };
        }
    }

    private handleDeckUpdate(deck: Deck & { totalCardCount: number; filteredCardCount: number; totalQuantity: number }) {
        this.deck = deck;

        this.setLikeIconName();

        this.format = FormatFactory.create(deck, this.cardsService, null, true);
        // Get the focused card if any
        this.format.getFocused().subscribe(card => this.focusedCard.next(card as Card));

        this.invalidCards = new Set(this.format.validate().map(card => card.name));

        // Get our statistics data
        this.typeCompositions = this.decksService.typeCompositions(this.deck.id);
        this.colorStats = this.decksService.colorStats(this.deck.id).pipe(map(stats => [stats.W, stats.U, stats.B, stats.R, stats.G, stats.C]));
        this.cmcHisto = this.decksService.cmcStats(this.deck.id).pipe(map(stats => {
            // The range for cmc is 0-16, so our histogram needs that many slots
            // eslint-disable-next-line no-magic-numbers
            const cmcHisto = [...Array(17).keys()].reduce((o, c) => {
                o[c] = 0;
                return o;
            }, {});
            for (const cmc of stats.cmc) {
                if (cmcHisto.hasOwnProperty(cmc)) {
                    cmcHisto[cmc] += 1;
                } else {
                    cmcHisto[cmc] = 1;
                }
            }
            return cmcHisto;
        }));
        // 'Red', 'Blue', 'Black', 'Green', 'White', 'Colorless'
        this.manaBalance = this.decksService.cmcStats(this.deck.id).pipe(map(stats => [
            {
                label: 'Consumers',
                data: [stats.sink.R, stats.sink.U, stats.sink.B, stats.sink.G, stats.sink.W, stats.sink.C],
                color: '#0000ffbb'
            },
            {
                label: 'Producers',
                data: [stats.source.R, stats.source.U, stats.source.B, stats.source.G, stats.source.W, stats.source.C],
                color: '#ff0000'
            }
        ]));
    }

    public forceUpdate() {
        if (!this.deck) {
            return;
        }
        this.decksService.get(this.deck.id, this.deckQuery(this.queryUpdated.value), Utils.PageOrDefault(this.pageUpdated.value))
            .subscribe(this.handleDeckUpdate.bind(this));
    }

    public setLikeIconName() {
        if (!this.currentUser || !this.deck || this.currentUser.upvotedDeckIds.indexOf(this.deck.id) === -1) {
            this.likeIconName = 'favorite_border';
        } else {
            this.likeIconName = 'favorite';
        }
    }

    public toggleLikeDeck() {
        if (this.authService.currentUserId === 'anonymous') {
            this.router.navigate(['/signup', {isPlanner: true}]);
            return;
        }

        const updateForDeckLikes = () => {
            this.updateCurrentUser();
            this.forceUpdate();
        };

        if (this.currentUser.upvotedDeckIds.indexOf(this.deck.id) !== -1) {
            this.decksService.removeUpvoteFromDeck(this.deck.id, this.currentUser.id).subscribe(updateForDeckLikes);
        } else {
            this.decksService.addUpvoteToDeck(this.deck.id, this.currentUser.id).subscribe(updateForDeckLikes);
        }
    }
}
